import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, ShieldCheck, Key, Sparkles } from "lucide-react";

const PERM_GROUP_LABELS = {
  sales: "🛒 المبيعات",
  purchases: "📦 المشتريات",
  products: "📋 الأصناف",
  warehouses: "🏭 المستودعات",
  batches: "📦 الدفعات",
  stock_count: "📊 جرد المخزون",
  customers: "👥 العملاء",
  suppliers: "🏢 الموردون",
  vouchers: "🧾 السندات",
  cashboxes: "💰 الصناديق",
  expenses: "💸 المصاريف",
  banks: "🏦 البنوك",
  cheques: "📝 الشيكات",
  journal: "📖 قيود اليومية",
  statements: "📄 القوائم المالية",
  period_close: "🔒 إغلاق الفترة",
  reports: "📈 التقارير",
  hr: "👤 الموارد البشرية",
  payroll: "💵 الرواتب",
  users: "🔑 المستخدمون",
  companies: "🏢 الشركات",
  settings: "⚙️ الإعدادات",
  audit: "🔍 سجل التدقيق",
  backup: "💾 النسخ الاحتياطي",
  store: "🛍️ المتجر الإلكتروني",
};

const PERM_LABELS = {
  view: "عرض", create: "إضافة", edit: "تعديل", delete: "حذف",
  discount: "خصومات", return: "مرتجعات", export: "تصدير",
  price: "تعديل الأسعار", perform: "تنفيذ", approve: "اعتماد",
  run: "تشغيل", manage: "إدارة كاملة", pnl: "قائمة الدخل", commission: "العمولات",
};

const permLabel = (perm) => {
  const [, action] = perm.split(".");
  return PERM_LABELS[action] || action;
};

export default function CustomRoles() {
  const { currentCompany, companyId } = useAuth();
  const [roles, setRoles] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: [] });
  const [applyingTpl, setApplyingTpl] = useState(false);

  const load = async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        api.get("/custom-roles"),
        api.get("/custom-roles/permissions"),
      ]);
      setRoles(rRes.data || []);
      setGrouped(pRes.data.grouped || {});
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", permissions: [] }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description || "", permissions: r.permissions || [] }); setOpen(true); };

  const togglePerm = (perm) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter(p => p !== perm)
        : [...f.permissions, perm]
    }));
  };

  const toggleGroupAll = (group, allPerms) => {
    const allSelected = allPerms.every(p => form.permissions.includes(p));
    setForm(f => ({
      ...f,
      permissions: allSelected
        ? f.permissions.filter(p => !allPerms.includes(p))
        : Array.from(new Set([...f.permissions, ...allPerms]))
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("اسم الدور مطلوب"); return; }
    try {
      if (editing) {
        await api.put(`/custom-roles/${editing.id}`, form);
        toast.success("تم تعديل الدور");
      } else {
        await api.post("/custom-roles", form);
        toast.success("تم إنشاء الدور");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doDelete = async (r) => {
    if (!confirm(`حذف الدور "${r.name}"؟ سيتم إلغاء ربطه من كل المستخدمين.`)) return;
    try {
      await api.delete(`/custom-roles/${r.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const applyTemplate = async (templateKey = null) => {
    const bt = currentCompany?.business_type;
    if (!bt) { toast.error("اختر شركة أولاً"); return; }
    const label = templateKey ? "المتجر الإلكتروني" : bt;
    if (!confirm(`تطبيق قالب "${label}" — سيُنشئ مجموعة أدوار جاهزة. متابعة؟`)) return;
    setApplyingTpl(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/apply-template`, templateKey ? { template_key: templateKey } : {});
      if (data.skipped) {
        toast.info(data.reason || "لا يوجد قالب لهذا القطاع");
      } else {
        toast.success(`تم إنشاء ${data.created} دور جديد · تم تخطي ${data.skipped} موجود`);
      }
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setApplyingTpl(false); }
  };

  return (
    <div className="space-y-6" data-testid="custom-roles-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-primary" /> الأدوار المخصصة</h1>
          <p className="text-sm text-muted-foreground mt-1">ابنِ أدواراً بصلاحيات دقيقة تناسب فريقك — مثال: "كاشير بدون خصم" أو "محاسب للقراءة فقط"</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => applyTemplate("online_store")} disabled={applyingTpl} className="gap-2" data-testid="apply-store-template" title="أنشئ أدواراً جاهزة لفريق المتجر">
            <Sparkles className="w-4 h-4 text-emerald-500" /> أدوار المتجر
          </Button>
          <Button variant="outline" onClick={() => applyTemplate()} disabled={applyingTpl} className="gap-2" data-testid="apply-sector-template" title="أنشئ أدواراً جاهزة تناسب قطاع الشركة">
            <Sparkles className="w-4 h-4 text-amber-500" /> {applyingTpl ? "جاري التطبيق..." : "تطبيق قالب القطاع"}
          </Button>
          <Button onClick={openNew} className="gap-2" data-testid="custom-role-add"><Plus className="w-4 h-4" /> دور جديد</Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>الاسم</TableHead>
              <TableHead>الوصف</TableHead>
              <TableHead>عدد الصلاحيات</TableHead>
              <TableHead>أنشئ في</TableHead>
              <TableHead>إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">لا توجد أدوار مخصصة بعد. اضغط "دور جديد" لبناء أول دور.</TableCell></TableRow>}
            {roles.map(r => (
              <TableRow key={r.id} data-testid={`custom-role-row-${r.id}`}>
                <TableCell className="font-bold">
                  {r.name}
                  {r.template_source && <span className="ms-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-bold" title={`من قالب ${r.template_source}`}>📋 قالب</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-md truncate">{r.description || "—"}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-bold">{r.permissions?.length || 0}</span></TableCell>
                <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString("ar-EG")}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`custom-role-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => doDelete(r)} data-testid={`custom-role-del-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-primary" /> {editing ? "تعديل الدور" : "دور جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>اسم الدور</Label>
                <Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="مثال: كاشير بدون خصم" data-testid="custom-role-name" />
              </div>
              <div>
                <Label>الوصف (اختياري)</Label>
                <Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="اختصر مهمة هذا الدور" data-testid="custom-role-desc" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-bold">الصلاحيات</Label>
                <span className="text-xs text-muted-foreground">مختار: <span className="font-bold text-primary tabular-nums">{form.permissions.length}</span></span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(grouped).map(([group, perms]) => {
                  const allSelected = perms.every(p => form.permissions.includes(p));
                  const someSelected = perms.some(p => form.permissions.includes(p));
                  return (
                    <Card key={group} className="p-3" data-testid={`perm-group-${group}`}>
                      <div className="flex items-center justify-between mb-2 pb-2 border-b border-border">
                        <div className="font-bold text-sm">{PERM_GROUP_LABELS[group] || group}</div>
                        <button
                          type="button"
                          onClick={() => toggleGroupAll(group, perms)}
                          className={`text-[10px] px-2 py-0.5 rounded font-bold ${allSelected ? "bg-primary text-primary-foreground" : someSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}
                          data-testid={`perm-group-toggle-${group}`}
                        >
                          {allSelected ? "الكل ✓" : "تحديد الكل"}
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {perms.map(p => (
                          <label key={p} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent p-1 rounded" data-testid={`perm-${p}`}>
                            <Checkbox
                              checked={form.permissions.includes(p)}
                              onCheckedChange={() => togglePerm(p)}
                            />
                            <span>{permLabel(p)}</span>
                          </label>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="custom-role-save">{editing ? "حفظ التعديل" : "إنشاء"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
